import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Stack,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  Paper,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TextField,
  InputAdornment,
  Badge,
  Tooltip,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useIniciativasWithCache } from '../hooks/useIniciativasWithCache';
import { iniciativasAPI } from '../services/api';
import TablaDesarrolloNegocio from './TablaDesarrolloNegocio';
import KanbanComercial from './KanbanComercial';
import AccionesDN from './AccionesDN';
import './DesarrolloNegocio.css';

const DesarrolloNegocio = () => {
  // Funciones para manejar localStorage con migración
  const getFromLocalStorage = (key, defaultValue) => {
    try {
      const item = localStorage.getItem(`desarrolloNegocio_${key}`);
      if (item === null) {
        return defaultValue;
      }
      
      const parsedItem = JSON.parse(item);
      
      // Migración para arrays vacíos vs null/undefined
      if (Array.isArray(defaultValue)) {
        return Array.isArray(parsedItem) ? parsedItem : defaultValue;
      }
      
      return parsedItem;
    } catch (error) {
      console.warn(`Error al leer ${key} del localStorage:`, error);
      return defaultValue;
    }
  };

  const saveToLocalStorage = (key, value) => {
    try {
      localStorage.setItem(`desarrolloNegocio_${key}`, JSON.stringify(value));
    } catch (error) {
      console.warn(`Error al guardar ${key} en localStorage:`, error);
    }
  };

  // Estados con valores desde localStorage
  const [activeTab, setActiveTab] = useState(() => getFromLocalStorage('activeTab', 0));
  const [añosSeleccionados, setAñosSeleccionados] = useState(() => getFromLocalStorage('añosSeleccionados', [])); // [] significa "todos los años"
  
  // Estados para los filtros
  const [searchTerm, setSearchTerm] = useState(() => getFromLocalStorage('searchTerm', ''));
  const [estadosFiltro, setEstadosFiltro] = useState(() => getFromLocalStorage('estadosFiltro', []));
  const [areasFiltro, setAreasFiltro] = useState(() => getFromLocalStorage('areasFiltro', []));
  const [jefesFiltro, setJefesFiltro] = useState(() => getFromLocalStorage('jefesFiltro', []));
  const [lineaNegocioFiltro, setLineaNegocioFiltro] = useState(() => getFromLocalStorage('lineaNegocioFiltro', []));
  const [negocioFiltro, setNegocioFiltro] = useState(() => getFromLocalStorage('negocioFiltro', []));
  const [negociosFiltro, setNegociosFiltro] = useState(() => getFromLocalStorage('negociosFiltro', []));
  const [responsablesDHFiltro, setResponsablesDHFiltro] = useState(() => getFromLocalStorage('responsablesDHFiltro', []));
  const [probabilidadFiltro, setProbabilidadFiltro] = useState(() => {
    const stored = getFromLocalStorage('probabilidadFiltro', []);
    return Array.isArray(stored) ? stored : [];
  });
  const [desvioFiltro, setDesvioFiltro] = useState(() => {
    const stored = getFromLocalStorage('desvioFiltro', []);
    return Array.isArray(stored) ? stored : [];
  });
  
  // Hook personalizado para manejar iniciativas con caché para desarrollo de negocio
  const {
    iniciativas,
    loading: loadingIniciativas,
    error: errorIniciativas,
    refreshData,
    getCacheStats,
    hasValidCache,
    isCacheExpired
  } = useIniciativasWithCache({
    autoLoad: true,
    estadosFiltro: [],  // No filtrar por estados en el hook, el backend ya lo hace
    endpoint: 'desarrollo_negocio'
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Funciones wrapper para actualizar estado y localStorage
  const updateActiveTab = (value) => {
    setActiveTab(value);
    saveToLocalStorage('activeTab', value);
  };

  const updateAñosSeleccionados = (value) => {
    setAñosSeleccionados(value);
    saveToLocalStorage('añosSeleccionados', value);
  };

  const updateSearchTerm = (value) => {
    setSearchTerm(value);
    saveToLocalStorage('searchTerm', value);
  };

  const updateEstadosFiltro = (value) => {
    setEstadosFiltro(value);
    saveToLocalStorage('estadosFiltro', value);
  };

  const updateAreasFiltro = (value) => {
    setAreasFiltro(value);
    saveToLocalStorage('areasFiltro', value);
  };

  const updateJefesFiltro = (value) => {
    setJefesFiltro(value);
    saveToLocalStorage('jefesFiltro', value);
  };

  const updateLineaNegocioFiltro = (value) => {
    setLineaNegocioFiltro(value);
    saveToLocalStorage('lineaNegocioFiltro', value);
  };

  const updateNegocioFiltro = (value) => {
    setNegocioFiltro(value);
    saveToLocalStorage('negocioFiltro', value);
  };

  const updateNegociosFiltro = (value) => {
    setNegociosFiltro(value);
    saveToLocalStorage('negociosFiltro', value);
  };

  const updateResponsablesDHFiltro = (value) => {
    setResponsablesDHFiltro(value);
    saveToLocalStorage('responsablesDHFiltro', value);
  };

  const updateProbabilidadFiltro = (value) => {
    setProbabilidadFiltro(value);
    saveToLocalStorage('probabilidadFiltro', value);
  };

  const updateDesvioFiltro = (value) => {
    setDesvioFiltro(value);
    saveToLocalStorage('desvioFiltro', value);
  };

  // Funciones para obtener el display formateado de los valores
  const getEstadoDisplay = useCallback((estado) => {
    const estadoMap = {
      'oportunidad_identificada': 'Identificada',
      'en_preparacion_propuesta': 'En preparación',
      'entregada': 'Entregada',
      'stand_by': 'Stand by',
      'rechazada': 'Rechazada',
      'aprobada': 'Aprobada',
      'null': 'Sin estado'
    };
    return estadoMap[estado] || estado;
  }, []);

  const getProbabilidadDisplay = (probabilidad) => {
    const probabilidadMap = {
      'alta': 'Alta',
      'media': 'Media',
      'baja': 'Baja',
      'null': 'Sin especificar'
    };
    return probabilidadMap[probabilidad] || probabilidad;
  };

  // Funciones helper para manejar selección múltiple
  const isAllSelected = (selectedItems, allItems) => {
    return selectedItems.length === 0 || (allItems.length > 0 && selectedItems.length === allItems.length);
  };

  const handleSelectChange = (event, allItems, updateFunction, currentSelection) => {
    const value = event.target.value;
    
    // Si se hace clic en "all", alternar entre seleccionar todo y deseleccionar todo
    if (value[value.length - 1] === 'all') {
      // Si ya está todo seleccionado, desmarcar todos (usar '__NONE__')
      if (isAllSelected(currentSelection, allItems)) {
        updateFunction(['__NONE__']);
      } else {
        // Si no está todo seleccionado, seleccionar todos (array vacío)
        updateFunction([]);
      }
      return;
    }
    
    // Filtrar '__NONE__' del array de selecciones normales
    updateFunction(value.filter(v => v !== '__NONE__'));
  };

  const getRenderValue = (selectedItems, allItems, label) => {
    // Si contiene '__NONE__', mostrar "Ninguno"
    if (selectedItems.includes('__NONE__')) {
      return 'Ninguno';
    }
    
    if (selectedItems.length === 0) {
      return 'Todos';
    }
    
    if (isAllSelected(selectedItems, allItems)) {
      return 'Todos';
    }
    
    if (selectedItems.length === 1) {
      // Para estados, mostrar el display formateado
      if (label === 'Estados') {
        return getEstadoDisplay(selectedItems[0]);
      }
      // Para probabilidad, mostrar el display formateado
      if (label === 'Probabilidades') {
        return getProbabilidadDisplay(selectedItems[0]);
      }
      // Para desvíos, mostrar el display formateado
      if (label === 'desvíos') {
        const desvioMap = {
          'rojo': '🔴 Desviada',
          'amarillo': '🟡 Próxima',
          'verde': '🟢 Lejana',
          'gris': '⚪ No aplica'
        };
        return desvioMap[selectedItems[0]] || selectedItems[0];
      }
      return selectedItems[0];
    }
    
    return `${selectedItems.length} seleccionado${selectedItems.length > 1 ? 's' : ''}`;
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    updateAñosSeleccionados([]); // [] significa "todos los años"
    updateSearchTerm('');
    updateEstadosFiltro([]);
    updateAreasFiltro([]);
    updateJefesFiltro([]);
    updateLineaNegocioFiltro([]);
    updateNegocioFiltro([]);
    updateNegociosFiltro([]);
    updateResponsablesDHFiltro([]);
    updateProbabilidadFiltro([]);
    updateDesvioFiltro([]);
  };

  // Leer parámetros desde la URL al cargar el componente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(parseInt(tabParam));
    }

    // Leer otros filtros desde URL si están presentes
    const searchParam = params.get('search');
    const estadosParam = params.get('estados');
    const areasParam = params.get('areas');
    const jefesParam = params.get('jefes');
    const negocioParam = params.get('negocio');
    const responsablesDHParam = params.get('responsablesDH');
    const probabilidadParam = params.get('probabilidad');
    const añosParam = params.get('años');

    if (searchParam) updateSearchTerm(searchParam);
    if (estadosParam) updateEstadosFiltro(estadosParam.split(','));
    if (areasParam) updateAreasFiltro(areasParam.split(','));
    if (jefesParam) updateJefesFiltro(jefesParam.split(','));
    if (negocioParam) updateNegocioFiltro(negocioParam.split(','));
    if (responsablesDHParam) updateResponsablesDHFiltro(responsablesDHParam.split(','));
    if (probabilidadParam) updateProbabilidadFiltro(probabilidadParam.split(','));
    if (añosParam) updateAñosSeleccionados(añosParam.split(',').map(Number));
  }, [location.search]);

  // Función para forzar recarga de datos
  const recargarDatos = useCallback(async () => {
    try {
      await refreshData();
    } catch (error) {
      console.error('Error al recargar datos:', error);
    }
  }, [refreshData]);

  // Función para obtener el año de la iniciativa - usar campo ano directamente
  const getAñoIniciativa = useCallback((iniciativa) => {
    // En Desarrollo de Negocio, usamos el año de la fecha de aprobación como prioridad
    if (iniciativa.fecha_de_aprobacion_propuesta_firmada) {
      const fecha = new Date(iniciativa.fecha_de_aprobacion_propuesta_firmada);
      return fecha.getFullYear();
    }
    
    // Si no tiene fecha de aprobación, usar fecha de entrega prevista
    if (iniciativa.fecha_de_entrega_propuesta_prevista) {
      const fecha = new Date(iniciativa.fecha_de_entrega_propuesta_prevista);
      return fecha.getFullYear();
    }

    // Si no tiene fecha de entrega prevista, usar fecha de creación
    if (iniciativa.created_at) {
      const fecha = new Date(iniciativa.created_at);
      return fecha.getFullYear();
    }
    
    return null; // Se mostrará como "Sin especificar"
  }, []);

  // Función para filtrar iniciativas basándose en los filtros aplicados
  // Si excludeFilter es !== null, solo aplicamos año + searchTerm (para calcular opciones únicas)
  // Si excludeFilter es null, aplicamos todos los filtros (para visualización final)
  const getIniciativasFiltradas = useCallback((excludeFilter = null) => {
    return iniciativas.filter(iniciativa => {
      // Filtrar iniciativas de tipo genérica (SIEMPRE)
      if (iniciativa.tipo_de_iniciativa === 'generica') {
        return false;
      }
      
      // Filtrar iniciativas de tipo innovación innocard o innotrans (SIEMPRE)
      if (iniciativa.tipo_innovacion === 'innocard' || iniciativa.tipo_innovacion === 'innotrans') {
        return false;
      }
      
      // Filtro de años - SIEMPRE aplicado (es el filtro padre)
      if (añosSeleccionados.includes('__NONE__')) {
        return false;
      }
      if (añosSeleccionados.length > 0 && !añosSeleccionados.includes('__NONE__')) {
        const año = getAñoIniciativa(iniciativa);
        if (!añosSeleccionados.includes(año)) return false;
      }

      // Filtro de búsqueda por texto - SIEMPRE aplicado
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
          (iniciativa.nombre_de_la_oportunidad && iniciativa.nombre_de_la_oportunidad.toLowerCase().includes(searchLower)) ||
          (iniciativa.negocio_estructura_nombre && iniciativa.negocio_estructura_nombre.toLowerCase().includes(searchLower)) ||
          (iniciativa.tecnologia_nombre && iniciativa.tecnologia_nombre.toLowerCase().includes(searchLower)) ||
          (iniciativa.contacto_de_negocio && iniciativa.contacto_de_negocio.toLowerCase().includes(searchLower))
        );
        if (!matchesSearch) return false;
      }

      // Si excludeFilter !== null, solo filtramos por año + searchTerm
      // Esto es para calcular las opciones únicas disponibles de cada filtro
      if (excludeFilter !== null) {
        return true;
      }

      // A partir de aquí, solo se aplica si excludeFilter === null (visualización final)

      // Filtro de estados
      if (estadosFiltro.includes('__NONE__') && excludeFilter !== 'estados') {
        return false;
      }
      if (estadosFiltro.length > 0 && !estadosFiltro.includes('__NONE__')) {
        // El backend ya envía el estado como 'aprobada' cuando corresponde
        // Simplemente verificamos si el estado de la iniciativa está en el filtro
        if (!estadosFiltro.includes(iniciativa.estado)) return false;
      }

      // Filtro de áreas
      if (areasFiltro.includes('__NONE__') && excludeFilter !== 'areas') {
        return false;
      }
      if (areasFiltro.length > 0 && !areasFiltro.includes('__NONE__')) {
        if (!areasFiltro.some(area => iniciativa.tecnologia_nombre && iniciativa.tecnologia_nombre.includes(area))) return false;
      }

      // Filtro de responsables de negocio
      if (jefesFiltro.includes('__NONE__') && excludeFilter !== 'jefes') {
        return false;
      }
      if (jefesFiltro.length > 0 && !jefesFiltro.includes('__NONE__')) {
        if (!jefesFiltro.includes(iniciativa.contacto_de_negocio)) return false;
      }

      // Filtros de jerarquía de negocio - aplicar en cascada respetando la jerarquía
      let lineaNegocio = iniciativa.negocio_estructura?.linea_de_negocio || iniciativa.linea_de_negocio || iniciativa.negocio_estructura_linea_de_negocio;
      if (!lineaNegocio && typeof iniciativa.negocio_estructura === 'string') {
        lineaNegocio = iniciativa.negocio_estructura.split('>')[0].trim();
      }
      const negocio = iniciativa.negocio_estructura?.negocio || iniciativa.negocio || iniciativa.negocio_estructura_negocio;
      const subnegocio = iniciativa.negocio_estructura_nombre;

      if (lineaNegocioFiltro.includes('__NONE__') && excludeFilter !== 'lineaNegocio') {
        return false;
      }
      if (lineaNegocioFiltro.length > 0 && !lineaNegocioFiltro.includes('__NONE__')) {
        let lnCompare = lineaNegocio;
        if (typeof lnCompare === 'string' && lnCompare.includes('>')) {
          lnCompare = lnCompare.split('>')[0].trim();
        }
        const lineaNegocioComparar = lnCompare || 'null';
        if (!lineaNegocioFiltro.includes(lineaNegocioComparar)) return false;
      }
      if (negocioFiltro.includes('__NONE__') && excludeFilter !== 'negocio') {
        return false;
      }
      if (negocioFiltro.length > 0 && !negocioFiltro.includes('__NONE__')) {
        const negocioComparar = negocio || 'null';
        if (!negocioFiltro.includes(negocioComparar)) return false;
      }
      if (negociosFiltro.includes('__NONE__') && excludeFilter !== 'subnegocio') {
        return false;
      }
      if (negociosFiltro.length > 0 && !negociosFiltro.includes('__NONE__')) {
        const subNegocioComparar = subnegocio || 'null';
        if (!negociosFiltro.includes(subNegocioComparar)) return false;
      }

      // Filtro de responsables DH
      if (responsablesDHFiltro.includes('__NONE__') && excludeFilter !== 'responsablesDH') {
        return false;
      }
      if (responsablesDHFiltro.length > 0 && !responsablesDHFiltro.includes('__NONE__')) {
        if (!responsablesDHFiltro.includes(iniciativa.responsable_comercial_nombre)) return false;
      }

      // Filtro de probabilidad
      if (probabilidadFiltro.includes('__NONE__') && excludeFilter !== 'probabilidad') {
        return false;
      }
      if (probabilidadFiltro.length > 0 && !probabilidadFiltro.includes('__NONE__')) {
        if (!probabilidadFiltro.includes(iniciativa.probabilidad_de_aprobacion)) return false;
      }

      return true;
    });
  }, [iniciativas, añosSeleccionados, estadosFiltro, areasFiltro, jefesFiltro, lineaNegocioFiltro, negocioFiltro, negociosFiltro, responsablesDHFiltro, probabilidadFiltro, getAñoIniciativa, searchTerm]);

  // Funciones para obtener opciones únicas de los filtros basándose en las iniciativas filtradas
  const estadosUnicos = useMemo(() => {
    // Estados permitidos en Desarrollo de Negocio
    const estadosPermitidos = [
      'oportunidad_identificada',
      'en_preparacion_propuesta',
      'supervision',
      'entregada',
      'stand_by',
      'aprobado',
      'rechazada',
      'aprobada' // Estado virtual para iniciativas con fecha_de_aprobacion_propuesta_firmada
    ];
    
    // Obtener estados únicos de las iniciativas
    const estadosReales = [...new Set(getIniciativasFiltradas('estados').map(i => i.estado))].filter(Boolean);
    
    // Filtrar solo los estados permitidos que existen en los datos
    return estadosReales.filter(estado => estadosPermitidos.includes(estado)).sort();
  }, [getIniciativasFiltradas]);

  const areasUnicas = useMemo(() => {
    return [...new Set(getIniciativasFiltradas('areas').flatMap(i => i.tecnologia_nombre ? [i.tecnologia_nombre] : []))].filter(Boolean).sort();
  }, [getIniciativasFiltradas]);

  const jefesUnicos = useMemo(() => {
    return [...new Set(getIniciativasFiltradas('responsables').map(i => i.contacto_de_negocio))].filter(Boolean).sort();
  }, [getIniciativasFiltradas]);

  const lineasNegocioUnicas = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('lineaNegocio');
    const valores = new Set();
    
    iniciativasFiltradas.forEach(i => {
      let lineaNegocio = i.negocio_estructura?.linea_de_negocio || i.linea_de_negocio || i.negocio_estructura_linea_de_negocio;
      if (!lineaNegocio && typeof i.negocio_estructura === 'string') {
        lineaNegocio = i.negocio_estructura.split('>')[0].trim();
      }
      if (lineaNegocio) {
        if (typeof lineaNegocio === 'string' && lineaNegocio.includes('>')) {
          lineaNegocio = lineaNegocio.split('>')[0].trim();
        }
        valores.add(lineaNegocio);
      }
    });
    
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  const negociosUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('negocio');
    const valores = new Set();
    
    iniciativasFiltradas.forEach(i => {
      const negocio = i.negocio_estructura?.negocio || i.negocio || i.negocio_estructura_negocio;
      if (negocio) {
        valores.add(negocio);
      }
    });
    
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  const subNegociosUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('subnegocio');
    const valores = new Set();
    
    iniciativasFiltradas.forEach(i => {
      const subnegocio = i.negocio_estructura_nombre;
      if (subnegocio) {
        valores.add(subnegocio);
      }
    });
    
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  const responsablesDHUnicos = useMemo(() => {
    return [...new Set(getIniciativasFiltradas('responsablesDH').map(i => i.responsable_comercial_nombre))].filter(Boolean).sort();
  }, [getIniciativasFiltradas]);

  // Opciones para probabilidad basadas en los datos reales
  const probabilidadUnica = useMemo(() => {
    return [...new Set(getIniciativasFiltradas('probabilidad').map(i => i.probabilidad_de_aprobacion))].filter(Boolean).sort();
  }, [getIniciativasFiltradas]);

  // Calcular total de iniciativas filtradas para mostrar en la pestaña
  const totalIniciativasFiltradas = useMemo(() => {
    return getIniciativasFiltradas().length;
  }, [getIniciativasFiltradas]);

  // Obtener iniciativas filtradas para pasar a los componentes
  const iniciativasFiltradas = useMemo(() => {
    return getIniciativasFiltradas();
  }, [getIniciativasFiltradas]);

  // Calcular KPIs basándose en las iniciativas filtradas
  const kpisCalculados = useMemo(() => {
    const hoy = new Date();
    
    // Total de oportunidades (todas las filtradas)
    const total_oportunidades = iniciativasFiltradas.length;
    
    // Propuestas en preparación
    const propuestas_en_preparacion = iniciativasFiltradas.filter(i => 
      i.estado === 'en_preparacion_propuesta'
    ).length;
    
    // Propuestas desviadas: graves (fecha ya pasada) + medias (0-3 días para entrega)
    const propuestas_desviadas = iniciativasFiltradas.filter(i => {
      if (i.estado !== 'en_preparacion_propuesta') return false;
      if (!i.fecha_de_entrega_propuesta_prevista) return false;
      if (i.fecha_de_entrega_propuesta_real) return false; // ya entregada, no desviada
      const fechaPrevista = new Date(i.fecha_de_entrega_propuesta_prevista);
      fechaPrevista.setHours(0, 0, 0, 0);
      const hoyNorm = new Date(hoy);
      hoyNorm.setHours(0, 0, 0, 0);
      const diasDiferencia = Math.ceil((fechaPrevista - hoyNorm) / (1000 * 60 * 60 * 24));
      return diasDiferencia <= 3; // rojo (< 0) + amarillo (0-3 días)
    }).length;
    
    // Propuestas pendientes de aprobación (propuestas entregadas, esperando firma del cliente)
    const propuestas_pendientes_aprobacion = iniciativasFiltradas.filter(i => 
      i.estado === 'entregada'
    ).length;
    
    // Oportunidades aprobadas (tienen fecha_de_aprobacion_propuesta_firmada y no están rechazadas)
    // Número de oportunidades aprobadas (estado 'aprobada' enviado desde backend)
    const oportunidades_aprobadas = iniciativasFiltradas.filter(i => 
      i.estado === 'aprobada'
    ).length;
    
    // Importe de venta aprobado (oportunidades con estado 'aprobada')
    const importe_aprobado = iniciativasFiltradas
      .filter(i => i.estado === 'aprobada')
      .reduce((sum, i) => sum + (parseFloat(i.importe_total_cliente) || 0), 0);
    
    // Importe pendiente de aprobación (propuestas entregadas pendientes de aprobación)
    const importe_pendiente_aprobacion = iniciativasFiltradas
      .filter(i => i.estado === 'entregada')
      .reduce((sum, i) => sum + (parseFloat(i.importe_total_cliente) || 0), 0);
    
    return {
      total_oportunidades,
      propuestas_en_preparacion,
      propuestas_desviadas,
      propuestas_pendientes_aprobacion,
      oportunidades_aprobadas,
      importe_aprobado,
      importe_pendiente_aprobacion
    };
  }, [iniciativasFiltradas]);

  // Función helper para formatear moneda
  const formatCurrency = (amount) => {
    if (!amount) return '0 €';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(amount);
  };

  // Función para limpiar filtros que ya no son válidos cuando cambian los datos
  const limpiarFiltrosInvalidos = useCallback(() => {
    if (iniciativas.length === 0) return;

    // Limpiar estados que ya no existen
    const estadosValidosActuales = estadosFiltro.filter(estado => estadosUnicos.includes(estado));
    if (estadosValidosActuales.length !== estadosFiltro.length) {
      updateEstadosFiltro(estadosValidosActuales);
    }

    // Limpiar áreas que ya no existen
    const areasValidasActuales = areasFiltro.filter(area => areasUnicas.includes(area));
    if (areasValidasActuales.length !== areasFiltro.length) {
      updateAreasFiltro(areasValidasActuales);
    }

    // Limpiar responsables negocio que ya no existen
    const jefesValidosActuales = jefesFiltro.filter(jefe => jefesUnicos.includes(jefe));
    if (jefesValidosActuales.length !== jefesFiltro.length) {
      updateJefesFiltro(jefesValidosActuales);
    }

    // Limpiar líneas de negocio que ya no existen
    const lineasNegocioValidasActuales = lineaNegocioFiltro.filter(linea => lineasNegocioUnicas.includes(linea));
    if (lineasNegocioValidasActuales.length !== lineaNegocioFiltro.length) {
      updateLineaNegocioFiltro(lineasNegocioValidasActuales);
    }

    // Limpiar negocios que ya no existen
    const negociosValidosActuales = negocioFiltro.filter(negocio => negociosUnicos.includes(negocio));
    if (negociosValidosActuales.length !== negocioFiltro.length) {
      updateNegocioFiltro(negociosValidosActuales);
    }

    // Limpiar subnegocios que ya no existen
    const subNegociosValidosActuales = negociosFiltro.filter(subnegocio => subNegociosUnicos.includes(subnegocio));
    if (subNegociosValidosActuales.length !== negociosFiltro.length) {
      updateNegociosFiltro(subNegociosValidosActuales);
    }

    // Limpiar responsables DH que ya no existen
    const responsablesDHValidosActuales = responsablesDHFiltro.filter(responsable => responsablesDHUnicos.includes(responsable));
    if (responsablesDHValidosActuales.length !== responsablesDHFiltro.length) {
      updateResponsablesDHFiltro(responsablesDHValidosActuales);
    }
  }, [iniciativas, estadosFiltro, areasFiltro, jefesFiltro, lineaNegocioFiltro, negocioFiltro, negociosFiltro, responsablesDHFiltro, estadosUnicos, areasUnicas, jefesUnicos, lineasNegocioUnicas, negociosUnicos, subNegociosUnicos, responsablesDHUnicos, updateEstadosFiltro, updateAreasFiltro, updateJefesFiltro, updateLineaNegocioFiltro, updateNegocioFiltro, updateNegociosFiltro, updateResponsablesDHFiltro]);

  // Solo ejecutar la limpieza una vez después de cargar los datos
  useEffect(() => {
    if (iniciativas.length > 0) {
      limpiarFiltrosInvalidos();
    }
  }, [iniciativas.length]); // Solo cuando cambie la cantidad de iniciativas

  // Generar años disponibles basándose en el campo año de las iniciativas
  const añosDisponibles = useMemo(() => {
    const años = new Set();
    
    iniciativas.forEach(iniciativa => {
      const año = getAñoIniciativa(iniciativa);
      años.add(año); // Incluir null si existe
    });
    
    // Convertir a array y ordenar (null al final, años descendente)
    const añosArray = Array.from(años);
    return añosArray.sort((a, b) => {
      if (a === null) return 1; // null al final
      if (b === null) return -1; // null al final
      return b - a; // Años descendente (más reciente primero)
    });
  }, [iniciativas, getAñoIniciativa]);

  // Generar años disponibles (usar años de las iniciativas si están disponibles, sino fallback)
  const getAñosDisponiblesDisplay = () => {
    if (añosDisponibles.length > 0) {
      return añosDisponibles;
    }
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // Tema personalizado para integración con Bootstrap
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0d6efd', // Bootstrap primary color
      },
      secondary: {
        main: '#6c757d', // Bootstrap secondary color
      },
      success: {
        main: '#198754', // Bootstrap success color
      },
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            minWidth: 120,
            '&.Mui-selected': {
              color: '#0d6efd',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: '#0d6efd',
            height: 3,
          },
        },
      },
    },
  });

  const handleTabChange = (event, newValue) => {
    updateActiveTab(newValue);
  };

  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`desarrollo-negocio-tabpanel-${index}`}
        aria-labelledby={`desarrollo-negocio-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Fade in={true} timeout={300}>
            <Box>
              {children}
            </Box>
          </Fade>
        )}
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100vw', 
        overflow: 'hidden',
        px: { xs: 1, sm: 2, md: 3 },
        py: 2
      }}>

        {/* Navegación por Tabs */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: 0 }}>
            {/* KPIs Dashboard - Métricas de Desarrollo de Negocio */}
            <Box sx={{ 
              mb: 2, 
              display: 'grid', 
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
              gap: 1.5
            }}>
              {/* Total Oportunidades */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#0d6efd', mb: 0.5 }}>
                  {kpisCalculados.total_oportunidades}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Total Oportunidades
                </Typography>
              </Paper>
              
              {/* Propuestas en Preparación */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#ffc107', mb: 0.5 }}>
                  {kpisCalculados.propuestas_en_preparacion}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  En Preparación
                </Typography>
              </Paper>
              
              {/* Propuestas Desviadas */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#dc3545', mb: 0.5 }}>
                  {kpisCalculados.propuestas_desviadas}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Desviadas
                </Typography>
              </Paper>
              
              {/* Pendientes de Aprobación */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fd7e14', mb: 0.5 }}>
                  {kpisCalculados.propuestas_pendientes_aprobacion}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Pend. Aprobación
                </Typography>
              </Paper>
              
              {/* Oportunidades Aprobadas */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#198754', mb: 0.5 }}>
                  {kpisCalculados.oportunidades_aprobadas}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Aprobadas
                </Typography>
              </Paper>
              
              {/* Importe Aprobado */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#20c997', mb: 0.5 }}>
                  {formatCurrency(kpisCalculados.importe_aprobado)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Importe Aprobado
                </Typography>
              </Paper>
              
              {/* Importe Pendiente */}
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#6f42c1', mb: 0.5 }}>
                  {formatCurrency(kpisCalculados.importe_pendiente_aprobacion)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Importe Pendiente
                </Typography>
              </Paper>
            </Box>
            {/* Filtros comunes para ambos componentes - estilo Operaciones */}
            <Box sx={{ mt: '2rem', mb: '1rem', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              
              <TextField
                variant="outlined"
                placeholder="Buscar por nombre, negocio, área o responsable..."
                value={searchTerm}
                onChange={(e) => updateSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i className="fas fa-search"></i>
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ minWidth: 220, flex: 1, maxWidth: { xs: '100%', md: 300 } }}
              />

              {/* Filtro de Años (múltiple) - estilo Operaciones */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Año (Aprobación {'>'} Entrega {'>'} S.E.)</InputLabel>
                <Select
                  multiple
                  value={añosSeleccionados}
                  label="Año (Aprobación {'>'} Entrega {'>'} S. E.)"
                  onChange={(e) => handleSelectChange(e, getAñosDisponiblesDisplay(), updateAñosSeleccionados, añosSeleccionados)}
                  renderValue={() => getRenderValue(añosSeleccionados, getAñosDisponiblesDisplay(), 'años')}
                  sx={{
                    fontSize: '0.85rem',
                    '& .MuiSelect-select': {
                      padding: '8px 12px'
                    }
                  }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(añosSeleccionados, getAñosDisponiblesDisplay())}
                        indeterminate={añosSeleccionados.length > 0 && añosSeleccionados.length < getAñosDisponiblesDisplay().length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {getAñosDisponiblesDisplay().map((año) => (
                    <MenuItem key={año || 'null'} value={año} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={añosSeleccionados.length === 0 || añosSeleccionados.includes(año)} />
                      </ListItemIcon>
                      <ListItemText primary={año === null ? 'Sin especificar' : año} />
                      {año === new Date().getFullYear() && (
                        <Chip 
                          label="Actual"
                          size="small" 
                          sx={{ ml: 'auto', fontSize: '0.6rem', height: '16px' }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Estado</InputLabel>
                <Select
                  multiple
                  value={estadosFiltro}
                  label="Estado"
                  onChange={(e) => handleSelectChange(e, estadosUnicos, updateEstadosFiltro, estadosFiltro)}
                  renderValue={() => getRenderValue(estadosFiltro, estadosUnicos, 'estados')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(estadosFiltro, estadosUnicos)}
                        indeterminate={estadosFiltro.length > 0 && estadosFiltro.length < estadosUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {estadosUnicos.map(estado => (
                    <MenuItem key={estado} value={estado} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={estadosFiltro.length === 0 || estadosFiltro.includes(estado)} />
                      </ListItemIcon>
                      <ListItemText primary={getEstadoDisplay(estado)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Probabilidad</InputLabel>
                <Select
                  multiple
                  value={probabilidadFiltro}
                  label="Probabilidad"
                  onChange={(e) => handleSelectChange(e, probabilidadUnica, updateProbabilidadFiltro, probabilidadFiltro)}
                  renderValue={() => getRenderValue(probabilidadFiltro, probabilidadUnica, 'probabilidades')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(probabilidadFiltro, probabilidadUnica)}
                        indeterminate={probabilidadFiltro.length > 0 && probabilidadFiltro.length < probabilidadUnica.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {probabilidadUnica.map(prob => (
                    <MenuItem key={prob} value={prob} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={probabilidadFiltro.length === 0 || probabilidadFiltro.includes(prob)} />
                      </ListItemIcon>
                      <ListItemText primary={getProbabilidadDisplay(prob)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Área conocimiento</InputLabel>
                <Select
                  multiple
                  value={areasFiltro}
                  label="Área conocimiento"
                  onChange={(e) => handleSelectChange(e, areasUnicas, updateAreasFiltro, areasFiltro)}
                  renderValue={() => getRenderValue(areasFiltro, areasUnicas, 'áreas')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(areasFiltro, areasUnicas)}
                        indeterminate={areasFiltro.length > 0 && areasFiltro.length < areasUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {areasUnicas.map(area => (
                    <MenuItem key={area} value={area} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={areasFiltro.length === 0 || areasFiltro.includes(area)} />
                      </ListItemIcon>
                      <ListItemText primary={area} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Línea de Negocio</InputLabel>
                <Select
                  multiple
                  value={lineaNegocioFiltro}
                  label="Línea de Negocio"
                  onChange={(e) => handleSelectChange(e, lineasNegocioUnicas, updateLineaNegocioFiltro, lineaNegocioFiltro)}
                  renderValue={() => getRenderValue(lineaNegocioFiltro, lineasNegocioUnicas, 'líneas de negocio')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(lineaNegocioFiltro, lineasNegocioUnicas)}
                        indeterminate={lineaNegocioFiltro.length > 0 && lineaNegocioFiltro.length < lineasNegocioUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {lineasNegocioUnicas.map(linea => (
                    <MenuItem key={linea} value={linea} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={lineaNegocioFiltro.length === 0 || lineaNegocioFiltro.includes(linea)} />
                      </ListItemIcon>
                      <ListItemText primary={linea} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Subnegocio</InputLabel>
                <Select
                  multiple
                  value={negociosFiltro}
                  label="Subnegocio"
                  onChange={(e) => handleSelectChange(e, subNegociosUnicos, updateNegociosFiltro, negociosFiltro)}
                  renderValue={() => getRenderValue(negociosFiltro, subNegociosUnicos, 'subnegocios')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(negociosFiltro, subNegociosUnicos)}
                        indeterminate={negociosFiltro.length > 0 && negociosFiltro.length < subNegociosUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {subNegociosUnicos.map(subnegocio => (
                    <MenuItem key={subnegocio} value={subnegocio} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={negociosFiltro.length === 0 || negociosFiltro.includes(subnegocio)} />
                      </ListItemIcon>
                      <ListItemText primary={subnegocio} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Responsable DN</InputLabel>
                <Select
                  multiple
                  value={responsablesDHFiltro}
                  label="Responsable DN"
                  onChange={(e) => handleSelectChange(e, responsablesDHUnicos, updateResponsablesDHFiltro, responsablesDHFiltro)}
                  renderValue={() => getRenderValue(responsablesDHFiltro, responsablesDHUnicos, 'responsables DH')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(responsablesDHFiltro, responsablesDHUnicos)}
                        indeterminate={responsablesDHFiltro.length > 0 && responsablesDHFiltro.length < responsablesDHUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {responsablesDHUnicos.map(responsable => (
                    <MenuItem key={responsable} value={responsable} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={responsablesDHFiltro.length === 0 || responsablesDHFiltro.includes(responsable)} />
                      </ListItemIcon>
                      <ListItemText primary={responsable} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro de Desvío */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Desvío</InputLabel>
                <Select
                  multiple
                  value={desvioFiltro}
                  label="Desvío"
                  onChange={(e) => handleSelectChange(e, ['rojo', 'amarillo', 'verde', 'gris'], updateDesvioFiltro, desvioFiltro)}
                  renderValue={() => getRenderValue(desvioFiltro, ['rojo', 'amarillo', 'verde', 'gris'], 'desvíos')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(desvioFiltro, ['rojo', 'amarillo', 'verde', 'gris'])}
                        indeterminate={desvioFiltro.length > 0 && desvioFiltro.length < 4}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  <MenuItem value="rojo" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('rojo')} />
                    </ListItemIcon>
                    <ListItemText primary="Fecha desviada" />
                  </MenuItem>
                  <MenuItem value="amarillo" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('amarillo')} />
                    </ListItemIcon>
                    <ListItemText primary="Fecha próxima" />
                  </MenuItem>
                  <MenuItem value="verde" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('verde')} />
                    </ListItemIcon>
                    <ListItemText primary="Fecha lejana" />
                  </MenuItem>
                  <MenuItem value="gris" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('gris')} />
                    </ListItemIcon>
                    <ListItemText primary="No aplica" />
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Botón Limpiar filtros */}
              <Button
                variant="contained"
                size="small"
                onClick={limpiarFiltros}
                sx={{ 
                  backgroundColor: '#dc3545',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
                  '&:hover': {
                    backgroundColor: '#c82333',
                    boxShadow: '0 4px 8px rgba(220, 53, 69, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                Limpiar filtros
              </Button>

              {/* Botón Recargar datos */}
              <Button
                variant="contained"
                size="small"
                onClick={recargarDatos}
                disabled={loadingIniciativas}
                sx={{ 
                  backgroundColor: hasValidCache ? '#6c757d' : '#007bff',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: hasValidCache ? '0 2px 4px rgba(108, 117, 125, 0.2)' : '0 2px 4px rgba(0, 123, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: hasValidCache ? '#5a6268' : '#0056b3',
                    boxShadow: hasValidCache ? '0 4px 8px rgba(108, 117, 125, 0.3)' : '0 4px 8px rgba(0, 123, 255, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  '&:disabled': {
                    backgroundColor: '#e9ecef',
                    color: '#6c757d'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
                title={(() => {
                  const stats = getCacheStats();
                  return stats.hasCache 
                    ? `Datos en caché desde: ${stats.lastFetched.toLocaleTimeString()}. Expira en ${stats.expiresIn} min.`
                    : 'Recargar datos desde el servidor';
                })()}
              >
                {loadingIniciativas ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                    Cargando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-rotate" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                    Actualizar
                  </>
                )}
              </Button>
            </Box>
            
            {/* Pestañas y botón de nueva iniciativa en la misma fila */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ 
                  '& .MuiTabs-flexContainer': {
                    gap: 2
                  },
                  '& .MuiTabs-indicator': {
                    bottom: 0
                  },
                  flex: 1
                }}
              >
                <Tab 
                  icon={<i className="fas fa-columns"></i>}
                  iconPosition="start"
                  label="Tablero" 
                  sx={{ 
                    minHeight: 60,
                    gap: 1
                  }}
                />
                <Tab 
                  icon={<i className="fas fa-table"></i>}
                  iconPosition="start"
                  label="Lista de oportunidades" 
                  sx={{ 
                    minHeight: 60,
                    gap: 1
                  }}
                />
                <Tab 
                  icon={<i className="fas fa-tasks"></i>}
                  iconPosition="start"
                  label="Acciones" 
                  sx={{ 
                    minHeight: 60,
                    gap: 1
                  }}
                />
              </Tabs>
              
              <Button
                variant="outlined"
                size="small"
                sx={{ 
                  backgroundColor: '#212529',
                  color: '#fff',
                  borderColor: '#212529',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: '#495057',
                    borderColor: '#212529',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                  },
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                  mb: { xs: 0, sm: 1 }
                }}
                onClick={() => navigate('/crear-oportunidad')}
              >
                + Nueva oportunidad
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Contenido de las pestañas */}
        <TabPanel value={activeTab} index={0}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <KanbanComercial 
              embedded={true} 
              añosFiltro={añosSeleccionados}
              searchTerm={searchTerm}
              estadosFiltro={estadosFiltro}
              negociosFiltro={negociosFiltro}
              lineaNegocioFiltro={lineaNegocioFiltro}
              probabilidadFiltro={probabilidadFiltro}
              iniciativas={iniciativasFiltradas}
              loading={loadingIniciativas}
              onRefresh={recargarDatos}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <TablaDesarrolloNegocio 
              embedded={true} 
              añosFiltro={añosSeleccionados}
              searchTerm={searchTerm}
              estadosFiltro={estadosFiltro}
              negociosFiltro={negociosFiltro}
              lineaNegocioFiltro={lineaNegocioFiltro}
              probabilidadFiltro={probabilidadFiltro}
              desvioFiltroProp={desvioFiltro}
              iniciativas={iniciativasFiltradas}
              loading={loadingIniciativas}
              totalIniciativas={totalIniciativasFiltradas}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <AccionesDN />
          </Paper>
        </TabPanel>

        {/* Footer con información adicional */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#6c757d' }}>
            💡 Tip: Usa el tablero para gestión visual de oportunidades, la lista para análisis detallado y Acciones para el seguimiento de actividades del equipo
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DesarrolloNegocio;